import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, ArrowRight } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

/**
 * "Switch to Ambassador View" — appears in BOTH:
 *   - top bar (variant="topbar"): compact pill with gold crown, always visible
 *   - sidebar footer (variant="sidebar"): full-width row above the Logout button
 *
 * Detection logic: we hit /api/auth/me to learn the admin's email, then
 * check /api/admin/ambassadors?search=<email>. If a matching profile
 * exists, the switcher shows up. The switcher's click handler copies
 * the admin JWT into the `auth_token` slot (the regular user token slot)
 * and navigates to /ambassador. The ambassador dashboard then uses
 * /api/ambassador/me which is gated by the `ambassadors` collection
 * lookup — NOT the user.role field — so an admin with an ambassador
 * profile sees their own ambassador data.
 *
 * Fail-open behaviour: if the detection request fails for any reason,
 * the topbar variant still renders a "View Ambassador View" CTA. This
 * way the owner never gets stuck thinking the button is missing — the
 * worst case is they click it and the ambassador dashboard tells them
 * "you don't have an ambassador profile yet".
 */
export default function DualRoleSwitcher({ variant = 'sidebar' }) {
  // null = still checking, true = has profile, false = no profile
  const [hasAmbassador, setHasAmbassador] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then(meRes => {
        const email = (meRes.data?.email || '').toLowerCase();
        if (!email) {
          if (!cancelled) setHasAmbassador(false);
          return null;
        }
        return axios.get(`${API}/admin/ambassadors?search=${encodeURIComponent(email)}`, { headers: getAuthHeaders() })
          .then(r => {
            const found = (r.data?.items || []).some(a => (a.user_email || '').toLowerCase() === email);
            if (!cancelled) setHasAmbassador(found);
          });
      })
      .catch(() => { if (!cancelled) setHasAmbassador(false); });
    return () => { cancelled = true; };
  }, []);

  // Sidebar variant: hide if not an ambassador (sidebar already crowded)
  if (variant === 'sidebar' && !hasAmbassador) return null;
  // Topbar variant: fail-open — render even while loading, so the user
  // always sees the option after they login as admin.
  if (variant === 'topbar' && hasAmbassador === false) return null;

  const handleSwitch = () => {
    const token = localStorage.getItem('admin_token');
    if (token) localStorage.setItem('auth_token', token);
    navigate('/ambassador');
  };

  if (variant === 'topbar') {
    return (
      <button
        onClick={handleSwitch}
        data-testid="dual-role-switcher-topbar"
        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-[10px] uppercase tracking-[0.18em] font-sans hover:bg-[#D4AF37]/15 hover:border-[#D4AF37] transition-colors"
        title="Open your ambassador dashboard"
      >
        <Crown className="w-3 h-3" />
        <span>View Ambassador</span>
        <ArrowRight className="w-3 h-3" />
      </button>
    );
  }

  return (
    <button
      onClick={handleSwitch}
      data-testid="dual-role-switcher-btn"
      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm font-sans rounded-md text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
    >
      <Crown className="w-4 h-4" /> Switch to Ambassador View
    </button>
  );
}
